import React from 'react'
import Decimal from 'decimal.js'
import { useDispatch, useSelector } from 'react-redux'
import { Observable } from 'rx'
import {
  isAllUpSelection,
  isKambiSportsSelection,
  isNoveltyBetType,
  isSameRaceMultiSelection,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import type {
  AllUpSelection,
  BettingType,
  Campaign,
  KambiSportsEventDetails,
  Selection,
  ToteSelection,
} from '@mobi/betslip/types'
import { Grid, GridCell } from '@mobi/component-library/Legacy/Grid'
import { AppRoutes } from '@core/AppRoutes'
import { openDepositModal } from '@core/Areas/Deposit'
import { calculateCombinations as favouriteNumbersCombinations } from '@core/Areas/FavouriteNumbers/calculator'
import { selectIsBusy } from '@core/Areas/QuickDeposit/Store/selectors'
import { QUICKBET_MODAL_ID } from '@core/Areas/Quickbet/constants'
import { type QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { getKambiSelectionDetailsForTracking } from '@core/Areas/Quickbet/helpers/analytics/getKambiSelectionDetails'
import {
  confirmBetAsync,
  confirmKambiBetAsync,
  confirmMultiBetAsync,
  proposeBetAsync,
  proposeKambiBetAsync,
  proposeMultiBetAsync,
} from '@core/Areas/Quickbet/helpers/betting'
import { calculateBetCost, calculateFlexiAmount } from '@core/Areas/Quickbet/helpers/calculator'
import { createBetRequest } from '@core/Areas/Quickbet/helpers/request'
import {
  AddingToBetslip,
  ChangeInvestment,
  DepositFundsDisplayed,
  InsufficientFundsForBet,
  KeepRaceCardSelections,
  ProposeKambiBetSucceeded,
  ProposeMultiBetSucceeded,
  SetInvalidInvestmentNotification,
} from '@core/Areas/Quickbet/signals'
import { trackShareBetLinkPlaced } from '@core/Areas/ShareBet/helpers/analytics'
import { calculateCombinations as tippingCombinations } from '@core/Areas/Tipping/helpers/calculator'
import { observeImmutable } from '@core/Components/HOCs'
import { closeModal } from '@core/Components/Modal'
import { isFavouriteNumbersSelection, isToteSportsSelection } from '@core/Data/Betting/selections'
import { trackBonusBetEvent } from '@core/Services/Analytics/helpers/trackers'
import { keys } from '@core/Services/Analytics/keys'
import {
  UserAccountState,
  state$ as userAccountState$,
} from '@core/State/UserAccount/userAccountDriver'
import { handleBetSelection } from '@core/Utils/betting/handleBetSelection'
import { getLegTypeIneligibilityDisplay } from '@core/Utils/betting/superPicks/helpers/getLegTypeIneligibilityDisplay'
import { checkSuperPickEligible } from '@core/Utils/betting/superPicks/helpers/rewardCalculator'
import type { BetSpecialOffer } from '@core/Utils/betting/superPicks/models/BetSpecialOffer'
import { Stake } from '@core/Utils/betting/superPicks/models/Stake'
import { appHistory } from '@core/Utils/routing/history'
import { InvestmentState, state$ as investmentState$ } from '../BetInvestment/driver'
import { FormulaState, state$ as formulaState$ } from '../Formula/driver'
import { SuperPickState, state$ as superPickState$ } from '../SuperPick/Components/SuperPick/driver'
import {
  BettingButton,
  BusyButton,
  CloseButton,
  DepositButton,
  EditBetButton,
  KeepBetsButton,
  LoginButton,
  NextEventButton,
  PendingBetsButton,
} from './Components/Buttons'
import { QuickbetBetButtonStyled } from './Components/Common.styles'
import { calculateCostShortfall } from './helpers/calculateCostShortfall'

export const ButtonsContainerComponent: React.FC<ButtonsContainerComponentProps> = ({
  id,
  isBusy,
  canBet,
  canProposeBet,
  canConfirmBet,
  notificationType,
  isEachWayAvailable,
  isEachWay,
  selection,
  selectionDetails,
  selectionMultiLegs,
  selectedKambiReward,
  bettingType,
  investmentWin,
  investmentPlace,
  isLoggedIn,
  selectedSuperPickOffer,
  selectedCampaign,
  isSuperPickSelectionInvalid,
  formulas,
  numberOfCombinationsSelected,
  shouldAllowPlaceInvestment,
  promptForInvestment,
  accountNumber,
  accountBalance,
  bonusBetBalance,
  bonusCashBalance,
  isUsingBonusCash,
  isUsingBonusBet,
  betSource,
  betSourceShareOrigin,
  copiedBetLoopPostId,
}) => {
  const dispatch = useDispatch()

  const isTote = isToteSelection(selection)
  const isAllUp = isTote && isAllUpSelection(selection)
  const isConfirmInProgressRef = React.useRef(false)

  const isMultiSelection = selectionMultiLegs && selectionMultiLegs.length > 0

  const isKambiSports = !isTote && !isMultiSelection && isKambiSportsSelection(selection)

  const numberOfCombinations = isAllUp
    ? numberOfCombinationsSelected
    : isTote
      ? (selection as ToteSelection).numberOfCombinations
      : isFavouriteNumbersSelection(selection)
        ? favouriteNumbersCombinations(selection.bets)
        : isToteSportsSelection(selection)
          ? tippingCombinations(selection.betSelections)
          : 1

  const betType = isTote ? (selection as ToteSelection).betType : undefined

  const betCost = calculateBetCost(
    isMultiSelection ? 'multi' : bettingType,
    investmentWin,
    investmentPlace,
    isEachWay,
    betType,
    numberOfCombinations
  )

  const isFlexiWithInsufficientInvestment =
    isTote &&
    isNoveltyBetType((selection as ToteSelection).betType) &&
    numberOfCombinations > 1 &&
    calculateFlexiAmount(investmentWin, numberOfCombinations) < 1

  const hasSuperPickOrFlexiError = (): boolean => {
    const stake = selectedSuperPickOffer
      ? Stake.normalise({ Win: Decimal(investmentWin || 0), Place: Decimal(investmentPlace || 0) })
      : undefined
    if (isFlexiWithInsufficientInvestment && investmentWin > 0) {
      SetInvalidInvestmentNotification({ type: 'InsufficientInvestmentForCombinations' })
      return true
    } else if (!!selectedSuperPickOffer && !checkSuperPickEligible(selectedSuperPickOffer, stake)) {
      setInvalidSuperPickSelectionNotification(selectedSuperPickOffer)
      return true
    } else {
      return false
    }
  }

  const handleAddToBetslipClick = () => {
    if (hasSuperPickOrFlexiError()) return
    AddingToBetslip()

    if (isMultiSelection) {
      handleBetSelection({
        betFlow: { location: 'BetSlip' },
        selections: selectionMultiLegs,
      })
    } else {
      handleBetSelection({
        betFlow: { location: 'BetSlip' },
        selections: [
          {
            bettingType: bettingType as BettingType,
            selection: isAllUp ? { ...selection, formulas } : (selection as Selection),
            selectionDetails,
            isEachWayAvailable,
            shouldAllowPlaceInvestment,
            betSource,
            betSourceShareOrigin: betSourceShareOrigin || undefined,
            copiedBetLoopPostId: copiedBetLoopPostId || undefined,
          },
        ],
        investmentWin,
        investmentPlace,
        isEachWay,
      })
    }

    dispatch(closeModal({ id: QUICKBET_MODAL_ID, exitAnimation: 'addToBetslip' }))
    trackBonusBet(keys.quickbetBonusBetAddedToBetslip)
  }

  const isBonusRewardSelected = selectedCampaign?.rewardType === 'BonusBetReward'

  const trackBonusBet = (eventKey: Parameters<typeof trackBonusBetEvent>[0]) => {
    if (selectedCampaign?.rewardType !== 'BonusBetReward') return
    const name = selectedCampaign.title
    const metaData = { name, accountNumber, accountBalance, bonusBetBalance, bonusCashBalance }
    trackBonusBetEvent(eventKey, metaData)
  }

  const handleProposeBetClick = () => {
    if (isKambiSports || isMultiSelection) {
      const shortfall = calculateCostShortfall(
        betCost,
        accountBalance,
        isUsingBonusCash,
        bonusCashBalance
      )

      if (shortfall > 0) {
        if (isKambiSports) ProposeKambiBetSucceeded()
        if (isMultiSelection) ProposeMultiBetSucceeded()
        InsufficientFundsForBet({ betCost, shortfall })
        return
      }
    }

    if (isKambiSports) {
      proposeKambiBetAsync({ selection })
      return
    }

    const shouldUseBonusCash = isUsingBonusCash && !!bonusCashBalance && bonusCashBalance > 0

    if (isMultiSelection) {
      proposeMultiBetAsync({
        items: selectionMultiLegs,
        investment: {
          value: investmentWin,
          isBonusBet: isBonusRewardSelected && !!investmentWin,
          isBonusCash: shouldUseBonusCash,
          bonusBetId: selectedCampaign?.id,
        },
      })
      trackBonusBet(keys.quickbetBonusBetProposed)
      return
    }

    if (hasSuperPickOrFlexiError()) return

    const betRequest = createBetRequest({
      selection,
      id: id as string,
      investmentWin,
      investmentPlace,
      isEachWay,
      selectedSuperPickOffer,
      campaign: selectedCampaign,
      formulas,
      shouldUseBonusCash,
    })
    if (!betRequest) return

    proposeBetAsync(betRequest).then(newAccountBalance => {
      const balance = newAccountBalance ?? accountBalance
      const shortfall = calculateCostShortfall(betCost, balance, isUsingBonusCash, bonusCashBalance)
      if (shortfall > 0) InsufficientFundsForBet({ betCost, shortfall })
    })
    trackBonusBet(keys.quickbetBonusBetProposed)
  }

  const handleConfirmBetClick = () => {
    if (isConfirmInProgressRef.current) return

    if (isKambiSports) {
      isConfirmInProgressRef.current = true

      confirmKambiBetAsync({
        selection,
        selectedKambiReward,
        investmentWin,
        copiedBetLoopPostId:
          betSource === 'bet-loop' && copiedBetLoopPostId ? copiedBetLoopPostId : undefined,
      }).finally(() => {
        if (betSourceShareOrigin && betSource === 'share-bet') {
          const { betType, sport, legs } = getKambiSelectionDetailsForTracking(
            selection,
            selectionDetails as KambiSportsEventDetails
          )
          trackShareBetLinkPlaced({
            spend: investmentWin,
            raceCode: undefined,
            betType,
            has_special: false,
            legs: legs,
            sportCode: sport,
            share_origin: betSourceShareOrigin,
          })
        }
        isConfirmInProgressRef.current = false
      })
      return
    }

    const shouldUseBonusCash = isUsingBonusCash && !!bonusCashBalance && bonusCashBalance > 0

    if (isMultiSelection) {
      isConfirmInProgressRef.current = true
      confirmMultiBetAsync({
        items: selectionMultiLegs,
        investment: {
          value: investmentWin,
          isBonusBet: isBonusRewardSelected && !!investmentWin,
          isBonusCash: shouldUseBonusCash,
          bonusBetId: selectedCampaign?.id,
        },
        copiedBetLoopPostId:
          betSource === 'bet-loop' && copiedBetLoopPostId ? copiedBetLoopPostId : undefined,
      }).finally(() => {
        isConfirmInProgressRef.current = false
      })
      trackBonusBet(keys.betslipBonusBetConfirmed)
      return
    }

    const betRequest = createBetRequest({
      selection,
      id: id as string,
      investmentWin,
      investmentPlace,
      isEachWay,
      selectedSuperPickOffer,
      campaign: selectedCampaign,
      formulas,
      shouldUseBonusCash,
    })
    if (!betRequest) return

    if (betSource === 'bet-loop' && copiedBetLoopPostId) {
      betRequest.copiedBetLoopPostId = copiedBetLoopPostId
    }

    isConfirmInProgressRef.current = true
    confirmBetAsync(betRequest).finally(() => {
      isConfirmInProgressRef.current = false
    })
    trackBonusBet(keys.betslipBonusBetConfirmed)
  }

  const handleNextEventsClick = () => {
    dispatch(closeModal({ id: QUICKBET_MODAL_ID }))
    appHistory.push(AppRoutes.NextEvents[0])
  }

  const handleKeepButtonClick = () => {
    KeepRaceCardSelections()
    dispatch(closeModal({ id: QUICKBET_MODAL_ID }))
  }

  const handleDepositButtonClick = () => {
    openDepositModal('quick-deposit')
    DepositFundsDisplayed()
  }

  const isInvestmentError =
    'InsufficientFunds' === notificationType ||
    'InvalidSuperPickSelection' === notificationType ||
    'InsufficientInvestmentForCombinations' === notificationType
  const isMysteryQuickPick = bettingType === 'mystery-quick-pick'
  const isToteSports = bettingType === 'tote-sports-tipping'

  const shouldShowAddToBetSlip =
    !isInvestmentError &&
    !canConfirmBet &&
    canProposeBet &&
    canBet &&
    !isMysteryQuickPick &&
    !isToteSports &&
    !isSameRaceMultiSelection(selection) &&
    !isKambiSportsSelection(selection)

  const shouldShowBusyButton = (canProposeBet || canConfirmBet) && isBusy
  const shouldShowEditButton =
    (isInvestmentError || canConfirmBet) && isLoggedIn && promptForInvestment
  const shouldShowProposeButton = canProposeBet && isLoggedIn && !isBusy
  const shouldShowConfirmButton = canConfirmBet && isLoggedIn && !isBusy
  const shouldShowLoginButton = !isLoggedIn && !isBusy && !(notificationType === 'BettingClosed')
  const shouldShowPendingBetsButton = !canBet && isLoggedIn
  const shouldShowDepositButton = notificationType === 'InsufficientFunds'

  const shouldDisableBetButton =
    isInvestmentError || betCost <= 0 || (isMysteryQuickPick && !!isUsingBonusBet)

  return (
    <Grid padding='0.5rem'>
      {shouldShowAddToBetSlip && (
        <GridCell padding='0.5rem'>
          <QuickbetBetButtonStyled
            type='button'
            color='secondary'
            data-tid-quickbet-add-to-betslip
            onClick={handleAddToBetslipClick}
            disabled={!!isSuperPickSelectionInvalid}
          >
            Add to Bet Slip
          </QuickbetBetButtonStyled>
        </GridCell>
      )}

      {shouldShowEditButton && (
        <GridCell>
          <EditBetButton isDisabled={isBusy} onClick={ChangeInvestment} />
        </GridCell>
      )}

      {shouldShowBusyButton && (
        <GridCell>
          <BusyButton />
        </GridCell>
      )}

      {shouldShowProposeButton && (
        <GridCell>
          <BettingButton
            onClick={handleProposeBetClick}
            betCost={betCost}
            isDisabled={shouldDisableBetButton}
            label='Bet'
          />
        </GridCell>
      )}

      {shouldShowConfirmButton && (
        <GridCell>
          <BettingButton
            onClick={handleConfirmBetClick}
            betCost={betCost}
            isDisabled={shouldDisableBetButton}
            label='Confirm'
          />
        </GridCell>
      )}

      {shouldShowLoginButton && (
        <GridCell>
          <LoginButton />
        </GridCell>
      )}

      {shouldShowDepositButton && (
        <GridCell>
          <DepositButton onClick={handleDepositButtonClick}>Deposit</DepositButton>
        </GridCell>
      )}

      {shouldShowPendingBetsButton && (
        <GridCell>
          <PendingBetsButton isKambiSports={isKambiSports} />
        </GridCell>
      )}

      {!canBet && !isKambiSports && (
        <GridCell>
          {canKeepSelections(selection) ? (
            <KeepBetsButton onClick={handleKeepButtonClick} />
          ) : (
            <NextEventButton onClick={handleNextEventsClick} />
          )}
        </GridCell>
      )}

      {!canBet && (
        <GridCell>
          <CloseButton />
        </GridCell>
      )}
    </Grid>
  )
}

const buttonsState$ = Observable.combineLatest(
  quickbetState$,
  investmentState$,
  userAccountState$,
  superPickState$,
  formulaState$,
  (
    quickbetRecord,
    investmentRecord,
    userAccountRecord,
    superPickRecord,
    formulaRecord
  ): ButtonsContainerComponentProps => {
    const quickbet: QuickbetState = quickbetRecord.toJS()
    const investment: InvestmentState = investmentRecord.toJS()
    const superPick: SuperPickState = superPickRecord.toJS()
    const formula: FormulaState = formulaRecord.toJS()
    const user: UserAccountState = userAccountRecord.toJS()
    const specialOffers = superPick.specialOffers
    const selectedSuperPickOffer = specialOffers
      ? specialOffers.find(offer => offer.specialSeq === superPick.selectedSuperPickSeq)
      : undefined
    if (quickbet.isEachWayAvailable) {
      investment.place.value = quickbet.isEachWay
        ? investment.win.value
        : quickbet.shouldAllowPlaceInvestment
          ? investment.place.value
          : 0
    }
    let campaign = user.campaigns?.find(x => x.id == investment.bonusBet?.campaignId)

    if (!quickbet.isUsingBonusCash && !campaign) {
      // To disable bonus cash, we must remove the ID
      const bonusCashCampaign = user.campaigns?.find(x => x.rewardType === 'BonusCashReward')
      campaign = bonusCashCampaign && { ...bonusCashCampaign, id: 0 }
    }

    return {
      id: quickbet.id,
      isBusy: quickbet.isBusy,
      canBet: quickbet.canBet,
      canProposeBet: quickbet.canProposeBet,
      canConfirmBet: quickbet.canConfirmBet,
      notificationType: quickbet.notificationType,
      isEachWayAvailable: quickbet.isEachWayAvailable,
      isEachWay: quickbet.isEachWay,
      bettingType: quickbet.bettingType,
      investmentWin: investment.win.value,
      investmentPlace: investment.place.value,
      isLoggedIn: !!userAccountRecord.isLoggedIn,
      selection: quickbet.selection,
      selectionDetails: quickbet.selectionDetails,
      selectionMultiLegs: quickbet.selectionMultiLegs,
      selectedKambiReward: quickbet.selectedKambiReward,
      isSuperPickSelectionInvalid: !!superPick.invalidSelectionMessage,
      selectedSuperPickOffer,
      formulas: formula.formulas,
      numberOfCombinationsSelected: formula.numberOfCombinationsSelected,
      shouldAllowPlaceInvestment: quickbet.shouldAllowPlaceInvestment,
      selectedCampaign: campaign,
      promptForInvestment: quickbet.promptForInvestment,
      accountNumber: user.accountNumber,
      accountBalance: user.accountBalance,
      bonusBetBalance: user.bonusBetBalance,
      bonusCashBalance: user.bonusCashBalance,
      isUsingBonusCash: quickbet.isUsingBonusCash,
      isUsingBonusBet: quickbet.isUsingBonusBet,
      betSource: quickbet.betSource,
      betSourceShareOrigin: quickbet.betSourceShareOrigin,
      copiedBetLoopPostId: quickbet.copiedBetLoopPostId,
    }
  }
)

export const ButtonsContainer: React.ComponentClass = observeImmutable(
  buttonsState$,
  ({ record }) => {
    const quickDepositIsBusy = useSelector(selectIsBusy)
    return <ButtonsContainerComponent {...record} isBusy={quickDepositIsBusy || record.isBusy} />
  }
)

ButtonsContainer.displayName = 'ButtonsContainer'

// =============
// Local Helpers
// =============

function canKeepSelections(selection: Selection | null) {
  if (!selection) return false

  if (isToteSelection(selection)) {
    return isNoveltyBetType(selection.betType) || isAllUpSelection(selection)
  }

  return isSameRaceMultiSelection(selection)
}

function setInvalidSuperPickSelectionNotification(selectedSuperPickOffer: BetSpecialOffer): void {
  SetInvalidInvestmentNotification({
    type: 'InvalidSuperPickSelection',
    subtitle: getLegTypeIneligibilityDisplay(selectedSuperPickOffer) || '',
  })
}

// =====
// Types
// =====

type ButtonsContainerComponentProps = Pick<
  QuickbetState,
  | 'id'
  | 'isBusy'
  | 'canProposeBet'
  | 'canConfirmBet'
  | 'canBet'
  | 'notificationType'
  | 'selection'
  | 'selectionDetails'
  | 'selectionMultiLegs'
  | 'selectedKambiReward'
  | 'isEachWayAvailable'
  | 'isEachWay'
  | 'bettingType'
  | 'shouldAllowPlaceInvestment'
  | 'promptForInvestment'
  | 'betSource'
  | 'betSourceShareOrigin'
  | 'copiedBetLoopPostId'
> & {
  investmentWin: number
  investmentPlace: number
  isSuperPickSelectionInvalid?: boolean
  selectedSuperPickOffer?: BetSpecialOffer
  selectedCampaign?: Campaign
  formulas: AllUpSelection['formulas']
  numberOfCombinationsSelected: number
  isLoggedIn: boolean
  accountNumber: number | null
  accountBalance: number | null
  bonusBetBalance: number | null
  bonusCashBalance: number | null
  isUsingBonusCash?: boolean
  isUsingBonusBet?: boolean
}
